import React, { useState, useRef } from 'react';
import { MoreVertical } from 'lucide-react';
import clsx from 'clsx';
import {
  useGetVehicleMediaComments,
  useAddVehicleMediaComment,
  useUpdateVehicleMediaComment,
  useDeleteVehicleMediaComment,
} from '@/hooks/useVehicleMediaComments';
import { useDriverProfile } from '@/hooks/useDriverProfile';

interface MediaCommentsProps {
  vehicleId: string;
  mediaUrl: string;
}

const getInitials = (name: string) =>
  name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

const formatTime = (date: string | Date) => {
  const d = new Date(date);
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' at ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  );
};

const MediaComments: React.FC<MediaCommentsProps> = ({ vehicleId, mediaUrl }) => {
  const [newCommentText, setNewCommentText] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [addCommentError, setAddCommentError] = useState<string | null>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);

  const { data: driverProfile } = useDriverProfile();
  const currentUserId = driverProfile?.userId;

  const { data: commentsData, isLoading: isCommentsLoading } = useGetVehicleMediaComments(vehicleId, mediaUrl);
  const addCommentMutation = useAddVehicleMediaComment();
  const updateCommentMutation = useUpdateVehicleMediaComment();
  const deleteCommentMutation = useDeleteVehicleMediaComment();

  const comments = commentsData?.comments ?? [];

  const handleAddComment = async () => {
    if (!newCommentText.trim()) return;
    setAddCommentError(null);
    const text = newCommentText.trim();
    setNewCommentText('');
    try {
      await addCommentMutation.mutateAsync({ vehicleId, mediaUrl, text, userName: driverProfile?.name });
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch {
      setAddCommentError('Failed to add comment. Please try again.');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingCommentId || !editingCommentText.trim()) return;
    await updateCommentMutation.mutateAsync({ commentId: editingCommentId, text: editingCommentText.trim() });
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  const handleDeleteComment = async (commentId: string) => {
    setOpenMenuId(null);
    await deleteCommentMutation.mutateAsync(commentId);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 px-4 pt-3">
      {/* Backdrop to close open menu */}
      {openMenuId && (
        <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
      )}

      <h3 className="text-white font-semibold text-base mb-3">Comments</h3>

      {/* Input row */}
      <div className="flex gap-2 mb-2">
        <input
          value={newCommentText}
          onChange={(e) => setNewCommentText(e.target.value)}
          placeholder="Add a comment..."
          className="flex-1 bg-transparent border border-zinc-600 rounded-lg px-3 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-zinc-400"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleAddComment();
            }
          }}
        />
        <button
          onClick={handleAddComment}
          disabled={!newCommentText.trim()}
          className="bg-gray-800 border border-gray-600 rounded-lg px-4 py-2.5 text-white text-sm font-medium disabled:opacity-40 transition-opacity"
        >
          Add
        </button>
      </div>

      {addCommentError && (
        <p className="text-red-400 text-xs mb-2">{addCommentError}</p>
      )}

      {/* Comments list */}
      <div className="flex-1 overflow-y-auto space-y-2 pb-2">
        {isCommentsLoading ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-600 border-t-white" />
          </div>
        ) : comments.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-3">No comments yet</p>
        ) : (
          comments.map((comment, index) => {
            const isOwn = !!currentUserId && comment.userId === currentUserId;
            const isCommentDeleting = deleteCommentMutation.isPending && deleteCommentMutation.variables === comment.id;
            const isLast = index === comments.length - 1 && comments.length > 1;

            return (
              <div
                key={comment.id}
                className="rounded-xl p-3 relative border border-zinc-700"
                style={{
                  opacity: comment._optimistic || isCommentDeleting ? 0.5 : 1,
                  transition: 'opacity 0.15s',
                }}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-white font-semibold text-sm leading-tight">
                          {comment.user?.name || 'Unknown'}
                        </p>
                        <p className="text-gray-400 text-[11px] mt-0.5">
                          {formatTime(comment.createdAt)}
                        </p>
                      </div>

                      {isOwn && !comment._optimistic && editingCommentId !== comment.id && (
                        <div className="relative z-20">
                          {isCommentDeleting ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-500 border-t-white" />
                          ) : (
                            <button
                              onClick={() => setOpenMenuId(openMenuId === comment.id ? null : comment.id)}
                              className="text-gray-400 hover:text-white transition-colors p-0.5"
                            >
                              <MoreVertical size={16} />
                            </button>
                          )}

                          {openMenuId === comment.id && (
                            <div className={clsx(
                              'absolute right-0 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl overflow-hidden min-w-[110px]',
                              isLast ? 'bottom-full mb-1' : 'top-6',
                            )}>
                              <button
                                onClick={() => {
                                  setOpenMenuId(null);
                                  setEditingCommentId(comment.id);
                                  setEditingCommentText(comment.text);
                                }}
                                className="block w-full px-4 py-2.5 text-sm text-white text-left"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteComment(comment.id)}
                                disabled={deleteCommentMutation.isPending}
                                className="block w-full px-4 py-2.5 text-sm text-red-400 text-left disabled:opacity-50"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {editingCommentId === comment.id ? (
                      <div className="flex items-center gap-2 mt-2">
                        <input
                          value={editingCommentText}
                          onChange={(e) => setEditingCommentText(e.target.value)}
                          disabled={updateCommentMutation.isPending}
                          className="flex-1 bg-gray-700 text-white text-sm rounded-lg px-2.5 py-1.5 focus:outline-none disabled:opacity-50"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit();
                            if (e.key === 'Escape') setEditingCommentId(null);
                          }}
                          autoFocus
                        />
                        <button
                          onClick={handleSaveEdit}
                          disabled={updateCommentMutation.isPending || !editingCommentText.trim()}
                          className="text-white bg-gray-700 rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-50 flex items-center gap-1"
                        >
                          {updateCommentMutation.isPending && (
                            <div className="animate-spin rounded-full h-3 w-3 border-2 border-gray-500 border-t-white" />
                          )}
                          {updateCommentMutation.isPending ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingCommentId(null)}
                          disabled={updateCommentMutation.isPending}
                          className="text-gray-400 text-xs disabled:opacity-40"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <p className="text-gray-200 text-sm mt-1.5">{comment.text}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={commentsEndRef} />
      </div>
    </div>
  );
};

export default MediaComments;
